// src/stores/activityStore.js
import { create } from 'zustand';
import * as activityService from '../services/activityService';

const useActivityStore = create((set, get) => ({
  // State
  projectActivities: {},  // Organized by project ID
  taskActivities: {},     // Organized by task ID
  userActivities: [],     // User's recent activities
  isLoading: false,
  error: null,
  activitiesPage: 1,
  hasMoreActivities: true,
  
  // Reset pagination
  resetPagination: () => {
    set({
      activitiesPage: 1,
      hasMoreActivities: true
    });
  },
  
  // Fetch activities for a project
  fetchProjectActivities: async (projectId, params = {}, append = false) => {
    set(state => ({
      isLoading: true,
      error: null,
      activitiesPage: params.page || state.activitiesPage
    }));
    
    try {
      const response = await activityService.getProjectActivities(projectId, params);
      
      set(state => ({
        projectActivities: {
          ...state.projectActivities,
          [projectId]: append && state.projectActivities[projectId]
            ? [...state.projectActivities[projectId], ...response.data.activities]
            : response.data.activities
        },
        hasMoreActivities: response.meta.hasNextPage,
        activitiesPage: response.meta.currentPage,
        isLoading: false
      }));
      
      return response.data.activities;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch project activities'
      });
     console.log('error', error);
    }
  },
  
  // Load more project activities
  loadMoreProjectActivities: async (projectId, params = {}) => {
    if (!get().hasMoreActivities || get().isLoading) return [];
    
    const nextPage = get().activitiesPage + 1;
    return get().fetchProjectActivities(projectId, { ...params, page: nextPage }, true);
  },
  
  // Fetch activities for a task
  fetchTaskActivities: async (taskId, params = {}, append = false) => {
    set(state => ({
      isLoading: true,
      error: null,
      activitiesPage: params.page || state.activitiesPage
    }));
    
    try {
      const response = await activityService.getTaskActivities(taskId, params);
      
      set(state => ({
        taskActivities: {
          ...state.taskActivities,
          [taskId]: append && state.taskActivities[taskId]
            ? [...state.taskActivities[taskId], ...response.data.activities]
            : response.data.activities
        },
        hasMoreActivities: response.meta.hasNextPage,
        activitiesPage: response.meta.currentPage,
        isLoading: false
      }));
      
      return response.data.activities;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch task activities'
      });
     console.log('error', error);
    }
  },
  
  // Load more task activities
  loadMoreTaskActivities: async (taskId, params = {}) => {
    if (!get().hasMoreActivities || get().isLoading) return [];
    
    const nextPage = get().activitiesPage + 1;
    return get().fetchTaskActivities(taskId, { ...params, page: nextPage }, true);
  },
  
  // Fetch user's recent activities
  fetchUserActivities: async (params = {}, append = false) => {
    set(state => ({
      isLoading: true,
      error: null,
      activitiesPage: params.page || state.activitiesPage
    }));
    
    try {
      const response = await activityService.getUserActivities(params);
      
      set(state => ({
        userActivities: append
          ? [...state.userActivities, ...response.data.activities]
          : response.data.activities,
        hasMoreActivities: response.meta.hasNextPage,
        activitiesPage: response.meta.currentPage,
        isLoading: false
      }));
      
      return response.data.activities;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch user activities'
      });
     console.log('error', error);
    }
  },
  
  // Load more user activities
  loadMoreUserActivities: async (params = {}) => {
    if (!get().hasMoreActivities || get().isLoading) return [];
    
    const nextPage = get().activitiesPage + 1;
    return get().fetchUserActivities({ ...params, page: nextPage }, true);
  },
  
  // Clear error
  clearError: () => {
    set({ error: null });
  }
}));

export default useActivityStore;